import type { Directive } from 'vue'

/**
 * Minimal replacement for vue-the-mask's `v-mask`, which has no Vue 3 build.
 * Supports the placeholder tokens this app uses:
 *   #  a digit
 *   A  a letter
 *   *  a digit or letter
 * Any other character in the pattern is a literal and is inserted automatically
 * (so typing "0930" into '##:##' yields "09:30").
 */
const tokens: Record<string, RegExp> = {
  '#': /\d/,
  A: /[a-zA-Z]/,
  '*': /[a-zA-Z0-9]/,
}

export function applyMask(value: string, pattern: string): string {
  let out = ''
  let vi = 0

  for (let pi = 0; pi < pattern.length && vi < value.length; pi++) {
    const p = pattern[pi]
    const token = tokens[p]

    if (token) {
      // Skip input characters that cannot satisfy this token.
      while (vi < value.length && !token.test(value[vi])) vi++
      if (vi >= value.length) break
      out += value[vi++]
    } else {
      out += p
      // Let the user type the literal themselves without duplicating it.
      if (value[vi] === p) vi++
    }
  }

  return out
}

const format = (el: HTMLInputElement, pattern: string) => {
  const masked = applyMask(el.value, pattern)
  if (masked !== el.value) {
    el.value = masked
    // Keep v-model in sync: Vue listens for `input`.
    el.dispatchEvent(new Event('input', { bubbles: true }))
  }
}

export const vMask: Directive<HTMLElement, string> = {
  mounted(el, binding) {
    const pattern = binding.value
    if (!pattern) return

    const input = el instanceof HTMLInputElement ? el : el.querySelector('input')
    if (!input) return

    const handler = () => format(input, pattern)
    input.addEventListener('input', handler)
    // Remember it so unmounted() can detach cleanly.
    ;(el as HTMLElement & { _maskHandler?: () => void })._maskHandler = handler
    if (input.value) format(input, pattern)
  },

  unmounted(el) {
    const input = el instanceof HTMLInputElement ? el : el.querySelector('input')
    const handler = (el as HTMLElement & { _maskHandler?: () => void })._maskHandler
    if (input && handler) input.removeEventListener('input', handler)
  },
}

export default vMask
